import { registerMockAPI } from '~/mocks/mockApis';

import { useQuery } from '@tanstack/react-query';

import { axiosInstance } from '~/services';
import { transformToSelectOptions } from '~/utils/common/transform';
import { getAllTemperatureOptions, groupTemperatureByCenter } from '~/utils/receive/filter';

import { API_ENDPOINT, OPTION_ALL } from '~/constants';

import { receiveKeys } from './keys';

import type { TypeErrorResponseData } from '~/types/common.types';
import type {
  TypeApiResponse,
  TypeReturnDisposalFilterAllResponse,
  TypeReturnDisposalFilterOptions,
} from '~/types/receive.types';

const SCREEN_NAME = 'RTS_SCRAP_PROGRESS';

/**
 * 필터 옵션 응답 데이터를 SelectOption 형태로 변환
 * @note clusters/warehouses는 입고현황과 동일 구조라 기존 그룹핑 유틸을 재사용한다.
 *       처리방법/처리상태는 서버가 "전체"를 내려주지 않으므로 OPTION_ALL을 prepend한다.
 */
const formatReturnDisposalFilterOptions = (
  data: TypeReturnDisposalFilterAllResponse
): TypeReturnDisposalFilterOptions => {
  const groupedWarehouseByCenter = groupTemperatureByCenter(data.clusters, data.warehouses);

  return {
    center: transformToSelectOptions(data.clusters, 'clusterCode', 'clusterName'),
    warehouse: getAllTemperatureOptions(groupedWarehouseByCenter),
    groupedWarehouseByCenter,
    releaseCode: transformToSelectOptions(data.releaseCodes, 'releaseCode', 'releaseName'),
    keywordType: transformToSelectOptions(data.codeTypes, 'type', 'name'),
    dateType: transformToSelectOptions(data.dateTypes, 'type', 'name'),
    processMethod: [OPTION_ALL, ...transformToSelectOptions(data.inspectionProcessMethods, 'code', 'name')],
    processStatus: [OPTION_ALL, ...transformToSelectOptions(data.inspectionProcessStatuses, 'code', 'name')],
  };
};

/** [입고관리 > 회송/폐기 관리] 필터 옵션 조회 API */
export const fetchReturnDisposalFilterAll = async (): Promise<TypeReturnDisposalFilterOptions> => {
  const { data } = await axiosInstance.get<TypeApiResponse<TypeReturnDisposalFilterAllResponse>>(
    `${API_ENDPOINT.RECEIVE.GET_FILTER_ALL}?screenName=${SCREEN_NAME}`
  );
  return formatReturnDisposalFilterOptions(data.data);
};

/** [입고관리 > 회송/폐기 관리] 필터 옵션 조회 Query Hook */
export const useGetReturnDisposalFilterAllQuery = (options?: { enabled?: boolean; silent?: boolean }) => {
  return useQuery({
    queryKey: receiveKeys.filterAll(SCREEN_NAME),
    queryFn: fetchReturnDisposalFilterAll,
    staleTime: 1000 * 60 * 30, // 30분
    enabled: options?.enabled ?? true,
    meta: options?.silent ? { showGlobalErrorToast: false } : undefined,
  });
};

/** [입고관리 > 회송/폐기 관리] 필터 옵션 조회 Mock (공용 엔드포인트라 screenName 무관 응답) */
registerMockAPI<TypeApiResponse<TypeReturnDisposalFilterAllResponse> | TypeErrorResponseData>({
  method: 'get',
  endpoint: API_ENDPOINT.RECEIVE.GET_FILTER_ALL,
  label: '회송폐기관리/필터 옵션 조회',
  scenarios: [
    {
      name: '정상 응답',
      response: {
        data: {
          clusters: [
            { clusterCode: null, clusterName: '전체' },
            { clusterCode: 'CC02', clusterName: '김포' },
            { clusterCode: 'CC03', clusterName: '평택' },
            { clusterCode: 'CC04', clusterName: '창원' },
            { clusterCode: 'TPCC01', clusterName: '안산 FSC' },
            { clusterCode: 'MC01', clusterName: '컬리나우-DMC점' },
            { clusterCode: 'MC02', clusterName: '컬리나우-도곡점' },
            { clusterCode: 'MC03', clusterName: '컬리나우-서초점' },
            { clusterCode: 'MC04', clusterName: '컬리나우-송파점' },
          ],
          warehouses: [
            { warehouseCode: null, warehouseName: '전체', clusterCode: 'CC02' },
            { warehouseCode: 'GGH1', warehouseName: '김포상온', clusterCode: 'CC02' },
            { warehouseCode: 'GGM1', warehouseName: '김포냉장', clusterCode: 'CC02' },
            { warehouseCode: 'GGL1', warehouseName: '김포냉동', clusterCode: 'CC02' },
            { warehouseCode: null, warehouseName: '전체', clusterCode: 'CC03' },
            { warehouseCode: 'GPH1', warehouseName: '평택상온', clusterCode: 'CC03' },
            { warehouseCode: 'GPM1', warehouseName: '평택냉장', clusterCode: 'CC03' },
            { warehouseCode: 'GPL1', warehouseName: '평택냉동', clusterCode: 'CC03' },
            { warehouseCode: 'GPH3', warehouseName: '평택뷰티전처리', clusterCode: 'CC03' },
            { warehouseCode: null, warehouseName: '전체', clusterCode: 'CC04' },
            { warehouseCode: 'KCH1', warehouseName: '창원상온', clusterCode: 'CC04' },
            { warehouseCode: 'KCM1', warehouseName: '창원냉장', clusterCode: 'CC04' },
            { warehouseCode: 'KCL1', warehouseName: '창원냉동', clusterCode: 'CC04' },
            { warehouseCode: null, warehouseName: '전체', clusterCode: 'MC01' },
            { warehouseCode: 'MC01', warehouseName: '컬리나우-DMC점', clusterCode: 'MC01' },
            { warehouseCode: null, warehouseName: '전체', clusterCode: 'MC02' },
            { warehouseCode: 'MC02', warehouseName: '컬리나우-도곡점', clusterCode: 'MC02' },
            { warehouseCode: null, warehouseName: '전체', clusterCode: 'MC03' },
            { warehouseCode: 'MC03', warehouseName: '컬리나우-서초점', clusterCode: 'MC03' },
            { warehouseCode: null, warehouseName: '전체', clusterCode: 'MC04' },
            { warehouseCode: 'MC04', warehouseName: '컬리나우-송파점', clusterCode: 'MC04' },
            { warehouseCode: null, warehouseName: '전체', clusterCode: 'TPCC01' },
            { warehouseCode: 'TP01', warehouseName: '안산 FSC', clusterCode: 'TPCC01' },
          ],
          releaseCodes: [
            { releaseCode: null, releaseName: '전체' },
            { releaseCode: 'DIRECT_DELIVERY', releaseName: '직배송' },
            { releaseCode: 'PARCEL', releaseName: '택배' },
            { releaseCode: 'KURLY_PICKUP', releaseName: '컬리픽업' },
            { releaseCode: 'MILKRUN_PICKUP', releaseName: '밀크런픽업' },
            { releaseCode: 'ETC', releaseName: '기타' },
          ],
          codeTypes: [
            { type: 'RECEIVING_REQUEST_CODE', name: '입고요청번호' },
            { type: 'INVOICE_NO', name: '송장번호' },
            { type: 'GOODS_CODE', name: '상품코드' },
            { type: 'GOODS_NAME', name: '상품명' },
            { type: 'LOT_NUMBER', name: 'LOT번호' },
          ],
          dateTypes: [
            { type: 'ISSUE_REGISTERED_AT', name: '이슈등록일' },
            { type: 'SCRAP_PLANNED_AT', name: '회수예정일' },
            { type: 'RECEIVING_EXPECTED_DATE', name: '입고예정일' },
            { type: 'RECEIVING_REQUESTED_DATE', name: '요청등록일' },
          ],
          inspectionProcessMethods: [
            { code: 'APPLICATION_AWAITING', name: '신청대기' },
            { code: 'RTS', name: '회송' },
            { code: 'SCRAP', name: '폐기' },
            { code: 'CLAIM_REQUEST', name: '클레임신청' },
            { code: 'HELD', name: '보류' },
            { code: 'REPLACEMENT', name: '재입고' },
          ],
          inspectionProcessStatuses: [
            { code: 'PENDING_INPUT', name: '정보입력대기' },
            { code: 'IN_PROGRESS', name: '진행중' },
            { code: 'CLAIM_REQUESTED', name: '클레임신청' },
            { code: 'HELD', name: '보류' },
            { code: 'WAITING', name: '대기' },
            { code: 'COMPLETED', name: '완료' },
          ],
        },
      },
    },
    {
      name: '서버 에러 (500)',
      response: { error: { code: 500, message: '필터 옵션을 불러오지 못했습니다.' } },
      status: 500,
    },
  ],
});
